import { useCallback, useEffect, useRef, useState } from 'react';
import { getWeatherByPlace, type WeatherByPlace } from '../../api/weather';
import { toWeatherView } from '../../lib/weather-view';
import { useAuthStore } from '../../store/authStore';
import { WEATHER_ITEMS_DEFAULT, type WeatherItemKey, type WeatherPanelState } from './WeatherPanel';

/**
 * 날씨 한 곳을 읽어 화면 상태로 바꾼다 (2026-09-21 · 시안 v4).
 *
 * ★ **부르는 자리는 하나**(`GET /weather/by-place`) — 고정 줄과 위젯이 같은 값을 본다.
 * ★ **시간별 예보를 끈 쪽은 단기예보를 부르지 않는다** — `kinds` 를 좁혀 공급자 호출을 줄인다
 *   (서버는 캐시가 없을 때만 공급자를 부르고, 한도는 전 회사가 나눠 쓴다).
 * ★ 위치 갈래(`location`)와 값 갈래(`failed`)를 **섞지 않는다** — 화면이 다른 말을 해야 한다.
 */

/** 회사 주소를 고칠 수 있는 역할 — 서버(`PATCH /institution`)와 같은 기준. */
const COMPANY_ADMIN = new Set(['ADMIN', 'SYSTEM_ADMIN']);

export interface UseWeatherPlace {
  state: WeatherPanelState;
  /** 「다시 시도」. */
  reload: () => void;
  /** 주소를 고칠 수 있는 사람인가 — 「회사정보 열기」를 붙일지 정한다. */
  canEditAddress: boolean;
}

/**
 * `place` — 어디 날씨인가.
 *   · 안 주거나 `'mine'` → **내 사업장, 없으면 회사**(서버가 정한다 · 화면은 내 근무지를 모른다)
 *   · `'company'`        → 회사
 *   · 그 밖(숫자 글자)     → 그 사업장
 */
export function useWeatherPlace(input: { place?: string; items?: readonly WeatherItemKey[] } = {}): UseWeatherPlace {
  const { place, items = WEATHER_ITEMS_DEFAULT } = input;
  const worksiteId = place && place !== 'mine' && place !== 'company' ? place : undefined;
  const forceCompany = place === 'company';
  const wantsHours = items.includes('hours');
  const role = useAuthStore((s) => s.user?.role);
  const [state, setState] = useState<WeatherPanelState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const seq = useRef(0);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const my = ++seq.current;
    let alive = true;
    setState({ kind: 'loading' });
    getWeatherByPlace({
      ...(worksiteId ? { worksiteId } : {}),
      ...(forceCompany ? { place: 'company' as const } : {}),
      // 시간별 예보가 꺼져 있으면 단기예보(shrt)는 받지 않는다.
      kinds: wantsHours ? ['ncst', 'vsrt', 'shrt'] : ['ncst', 'vsrt'],
    })
      .then((res: WeatherByPlace) => {
        if (!alive || my !== seq.current) return;
        if (res.location.kind !== 'ok') {
          setState({ kind: 'location', location: res.location, place: res.place.kind });
          return;
        }
        const view = toWeatherView(res.weather?.results);
        // 값이 하나도 안 왔으면 「못 가져옴」이다 — 빈 카드를 그리지 않는다.
        if (!res.weather?.usable || view.now.tempC === null) {
          setState({ kind: 'failed', regionName: res.location.regionName });
          return;
        }
        setState({ kind: 'ready', regionName: res.location.regionName, view });
      })
      .catch(() => {
        if (alive && my === seq.current) setState({ kind: 'failed', regionName: null });
      });
    return () => { alive = false; };
  }, [worksiteId, forceCompany, wantsHours, tick]);

  return {
    state,
    reload,
    // 사업장 주소는 근태 담당이 고친다 — 화면은 «회사 주소»일 때만 「회사정보 열기」를 연다.
    canEditAddress: !worksiteId && role !== undefined && COMPANY_ADMIN.has(role),
  };
}
